import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import ConfirmModal from '../../../components/ui/ConfirmModal.jsx';
import { financialService } from '../../../services/api';
import transactionService from '../../../services/transactionService.js';

const TransactionTable = ({ selectedPeriod, selectedHotel, onEdit, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  useEffect(() => {
    loadTransactions();
  }, [selectedPeriod, selectedHotel, pagination.page]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder
      };

      if (selectedHotel?.id) {
        params.hotelId = selectedHotel.id;
      }

      // Calcular período baseado na seleção
      const now = new Date();
      if (selectedPeriod === 'currentMonth') {
        params.startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        params.endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString();
      } else if (selectedPeriod === 'lastMonth') {
        params.startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
        params.endDate = new Date(now.getFullYear(), now.getMonth(), 0).toISOString();
      } else if (selectedPeriod === 'last3Months') {
        params.startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString();
        params.endDate = now.toISOString();
      } else if (selectedPeriod === 'last6Months') {
        params.startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1).toISOString();
        params.endDate = now.toISOString();
      } else if (selectedPeriod === 'lastYear') {
        params.startDate = new Date(now.getFullYear() - 1, 0, 1).toISOString();
        params.endDate = new Date(now.getFullYear() - 1, 11, 31).toISOString();
      }

      const response = await financialService.getTransactions(params);
      
      setTransactions(response.transactions || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 0
      }));
    } catch (err) {
      console.error('Erro ao carregar transações:', err);
      setError('Erro ao carregar transações');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getTypeLabel = (type) => {
    return type === 'INCOME' ? 'Receita' : 'Despesa';
  };

  const getTypeColor = (type) => {
    return type === 'INCOME' ? 'text-success' : 'text-destructive';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      RESERVATION_PAYMENT: 'Pagamento de Reserva',
      SERVICE_FEE: 'Taxa de Serviço',
      OTHER_INCOME: 'Outras Receitas',
      MAINTENANCE: 'Manutenção',
      CLEANING_SUPPLIES: 'Material de Limpeza',
      UTILITIES: 'Utilidades',
      STAFF_SALARY: 'Salários',
      MARKETING: 'Marketing',
      ADMINISTRATIVE: 'Administrativo',
      OTHER_EXPENSE: 'Outras Despesas'
    };
    return labels[category] || category;
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = transaction.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transaction.reference?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || 
                       (filterType === 'income' && transaction.type === 'INCOME') ||
                       (filterType === 'expense' && transaction.type === 'EXPENSE');
    
    return matchesSearch && matchesType;
  });

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleEdit = (transaction) => {
    onEdit?.(transaction);
  };

  const handleDelete = async (transaction) => {
    setTransactionToDelete(transaction);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (!transactionToDelete) return;

    try {
      await transactionService.delete(transactionToDelete.id);
      onRefresh?.();
      loadTransactions();
      setShowConfirmModal(false);
      setTransactionToDelete(null);
    } catch (err) {
      console.error('Erro ao excluir transação:', err);
      alert('Erro ao excluir transação. Por favor, tente novamente.');
    }
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setTransactionToDelete(null);
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="h-12 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <div className="flex items-center space-x-3 text-destructive">
          <Icon name="AlertCircle" size={20} />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Transações Recentes</h3>
        <div className="flex items-center space-x-4">
            <Input
              placeholder="Buscar transações..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64"
            />
            <Select
              value={filterType}
              onChange={setFilterType}
              options={[
                { value: 'all', label: 'Todos' },
                { value: 'income', label: 'Receitas' },
                { value: 'expense', label: 'Despesas' }
              ]}
              className="w-32"
            />
        </div>
      </div>

      {filteredTransactions.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Receipt" size={48} className="mx-auto mb-4 opacity-50" />
          <p>Nenhuma transação encontrada</p>
        </div>
      ) : (
        <>
      <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Data</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Descrição</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Tipo</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Categoria</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">Valor</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Referência</th>
                  <th className="text-center py-3 px-4 font-medium text-muted-foreground">Ações</th>
            </tr>
          </thead>
          <tbody>
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4 text-sm text-foreground">
                      {formatDate(transaction.date)}
                </td>
                    <td className="py-3 px-4 text-sm text-foreground">
                      {transaction.description || 'Sem descrição'}
                </td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`font-medium ${getTypeColor(transaction.type)}`}>
                        {getTypeLabel(transaction.type)}
                  </span>
                </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {getCategoryLabel(transaction.category)}
                </td>
                    <td className="py-3 px-4 text-sm text-right">
                      <span className={`font-medium ${getTypeColor(transaction.type)}`}>
                        {transaction.type === 'EXPENSE' ? '-' : '+'}{formatCurrency(transaction.amount)}
                  </span>
                </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {transaction.reference || '-'}
                </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleEdit(transaction)}
                          className="p-1.5 rounded-lg hover:bg-primary/10 transition-smooth"
                          title="Editar transação"
                        >
                          <Icon name="Edit" size={16} color="var(--color-primary)" />
                        </button>
                        <button
                          onClick={() => handleDelete(transaction)}
                          className="p-1.5 rounded-lg hover:bg-destructive/10 transition-smooth"
                          title="Excluir transação"
                        >
                          <Icon name="Trash2" size={16} color="var(--color-destructive)" />
                        </button>
                      </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

          {/* Paginação */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
        <p className="text-sm text-muted-foreground">
                Mostrando {((pagination.page - 1) * pagination.limit) + 1} a{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
                {pagination.total} transações
        </p>
        <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  <Icon name="ChevronLeft" size={16} />
          </Button>
                <span className="text-sm text-muted-foreground">
                  Página {pagination.page} de {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  <Icon name="ChevronRight" size={16} />
          </Button>
        </div>
      </div>
          )}
        </>
      )}

      {/* Modal de Confirmação */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Excluir Transação"
        message={transactionToDelete ? 
          `Tem certeza que deseja excluir esta transação?\n\n` +
          `Tipo: ${getTypeLabel(transactionToDelete.type)}\n` +
          `Categoria: ${getCategoryLabel(transactionToDelete.category)}\n` +
          `Valor: ${formatCurrency(transactionToDelete.amount)}` 
          : ''
        }
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default TransactionTable;